import React from 'react';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { ExclamationCircleFill, ExclamationCircle } from 'react-bootstrap-icons';
import { formatMessage } from '../../../../translations/format-message';

interface IProps {
  values: any;
  setSelectedProcessId: (processId: number) => void;
  selectedProcessId: number;
}

const ProcessTabView = (props: IProps) => {

  const { values, setSelectedProcessId, selectedProcessId } = props;
  const hasDuplicateItemInSameHeader = (checkItems: any[]) => {
    if (!checkItems || !Array.isArray(checkItems)) return false;

    const itemsByHeader: { [key: string]: Set<string> } = {};
    for (const item of checkItems) {
      const messages =
        item?.CheckItems && Array.isArray(item.CheckItems)
          ? item.CheckItems
          : item?.CheckItemMessage && Array.isArray(item.CheckItemMessage)
            ? item.CheckItemMessage
            : [item];
      const header = String(item?.Title || item?.HeaderTitle || '').trim();

      if (!itemsByHeader[header]) {
        itemsByHeader[header] = new Set();
      }

      for (const msg of messages) {
        const text = String(
          msg?.CheckMessage ?? msg?.text ?? msg?.CheckItemMessage ?? ''
        )
          .trim()
          .toLowerCase();

        if (!text) continue;
        if (itemsByHeader[header].has(text)) return true;
        itemsByHeader[header].add(text);
      }
    }

    return false;
  };

  const hasCheckItemErrors = (checkItems: any[]) => {
    if (!checkItems || !Array.isArray(checkItems)) return false;
    for (const item of checkItems) {
      const isEmpty = !item.CheckMessage || item.CheckMessage.trim() === '';
      const isTouched = !!item.IsTouched;
      if (isTouched && isEmpty) return true;
    }
    return false;
  };

  const hasHeaderWithoutItemError = (processId: number, tabIndex: number) => {
    const pId = Number(processId);
    const tIdx = Number(tabIndex);
    if (pId <= 0 || tIdx <= 0) return false;
    const validationKey = `${pId}_${tIdx}`;
    return Boolean(values?.CheckItemHeaderValidationMap?.[validationKey]);
  };
  const hasDuplicateHeaderNameError = (processId: number, tabIndex: number) => {
    const pId = Number(processId);
    const tIdx = Number(tabIndex);
    if (pId <= 0 || tIdx <= 0) return false;
    const validationKey = `${pId}_${tIdx}`;
    return Boolean(values?.CheckItemDuplicateHeaderValidationMap?.[validationKey]);
  };

  return (
    <ul
      className="nav nav-tabs"
      id="processTab"
      role="tablist"
    >
      {values?.Process?.length > 0 &&
        values?.Process.filter(
          (pro) =>
            pro.ProcessId !== -1 &&
            pro.ProcessId !== -2 &&
            pro.ProcessId !== -3 &&
            pro.ProcessId !== -4
        ).map((proc) => {
          const hasDuplicateHeaderNameErrorInProcess = proc.fileIndices?.some((file: any) =>
            hasDuplicateHeaderNameError(proc.ProcessId, file.TabIndex)
          );
          const hasDuplicateError = proc.fileIndices?.some((file: any) =>
            hasDuplicateItemInSameHeader(file.CheckItems)
          );
          const hasMissingCheckItemError = proc.fileIndices?.some(
            (file: any) =>
              hasCheckItemErrors(file.CheckItems) ||
              (proc.ProcessId === selectedProcessId && Number(file.TabIndex) === Number(values?.activeIndex)
                ? false
                : hasHeaderWithoutItemError(proc.ProcessId, file.TabIndex))
          );
          const hasError = Boolean(
            hasDuplicateHeaderNameErrorInProcess || hasDuplicateError || hasMissingCheckItemError
          );
          const tooltipMessageId = hasDuplicateHeaderNameErrorInProcess
            ? 'duplicate_header_names_not_allowed'
            : hasDuplicateError
              ? 'duplicate_item_in_header'
              : 'please_add_check_item';

          const hasCorrectionRequest = Boolean(
            (proc?.CorrectionRequest && typeof proc?.CorrectionRequest === 'object' && !proc?.CorrectionRequest?.IsCompleted) ||
            proc?.CorrectionRequest === true ||
            (proc?.HasCorrectionRequest && !proc?.IsCorrectionCompleted)
          );

          return (
            <li
              key={proc?.ProcessId}
              className={`nav-item ${proc?.ProcessId == selectedProcessId ? 'active' : ''}`}
              role="presentation"
            >
              <button
                className={`nav-link ${proc?.ProcessId == selectedProcessId ? 'active' : ''} d-flex align-items-center gap-1 product-tabs-error`}
                id={`process-tab-${proc.ProcessId}`}
                data-bs-toggle="tab"
                data-bs-target="#home"
                type="button"
                role="tab"
                aria-controls="home"
                aria-selected="true"
                onClick={() => {
                  props.setSelectedProcessId(proc.ProcessId);
                }}
              >
                <span>{proc.ShortName}</span>
                {hasCorrectionRequest && (
                  <ExclamationCircleFill
                          className="text-danger"
                          title={formatMessage('correction_request')}
                        />
                )}
                {hasError && (
                  <OverlayTrigger
                    placement="top"
                    overlay={
                      <Tooltip id={`tooltip-error-${proc.ProcessId}`}>
                        {formatMessage(tooltipMessageId)}
                      </Tooltip>
                    }
                  >
                    <ExclamationCircle className="text-danger" />
                  </OverlayTrigger>
                )}
              </button>
            </li>
          );
        })}
    </ul>
  );
};

export default ProcessTabView;
